import React, { useState, useEffect, useCallback } from 'react';
import { useEntity } from '../contexts/EntityContext';
import { useLanguage } from '../contexts/LanguageContext';
import { API_URL } from '../config/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Upload, FileText, Check, Loader2, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const EXPENSE_ACCOUNTS = [
  { code: '4100', name: 'Hosting & infra' },
  { code: '4200', name: 'Uitbesteed werk' },
  { code: '4300', name: 'Overige directe kosten' },
  { code: '4400', name: 'Advertentiekosten' },
  { code: '7000', name: 'Salarissen en lonen' },
  { code: '7010', name: 'Sociale lasten' },
  { code: '7020', name: 'Pensioenlasten' },
  { code: '7100', name: 'Freelancers' },
  { code: '7500', name: 'Huisvestingskosten' },
  { code: '7600', name: 'Kantoorkosten' },
  { code: '7700', name: 'Software / ICT' },
  { code: '7800', name: 'Algemene kosten' },
  { code: '7900', name: 'Advieskosten' },
  { code: '8000', name: 'Afschrijvingen materieel' },
  { code: '8010', name: 'Afschrijvingen immaterieel' },
  { code: '8100', name: 'Rentelasten' },
  { code: '8200', name: 'Bankkosten' },
];

const REVENUE_ACCOUNTS = [
  { code: '4000', name: 'Omzet diensten' },
  { code: '4010', name: 'Omzet SaaS' },
  { code: '4020', name: 'Omzet consultancy' },
  { code: '4030', name: 'Overige omzet' },
];

// Balansrekeningen voor niet-omzet/kosten transacties (BTW teruggave, leningen, intercompany etc.)
const BALANCE_ACCOUNTS = [
  { code: '1300', name: 'Debiteuren (betaling)' },
  { code: '1350', name: 'Overige vorderingen' },
  { code: '1400', name: 'Crediteuren (betaling)' },
  { code: '1600', name: 'BTW teruggave / vordering' },
  { code: '1700', name: 'BTW afdracht' },
  { code: '1750', name: 'Vooruitontvangen omzet' },
  { code: '1900', name: 'Intercompany' },
  { code: '0300', name: 'Deelnemingen' },
  { code: '0500', name: 'Lening (aflossing/opname)' },
  { code: '0510', name: 'Achtergestelde lening' },
  { code: '1100', name: 'Spaarrekening' },
];

// Determine if an account is a balance sheet account (no VAT calculation needed)
function isBalanceAccount(code) {
  return BALANCE_ACCOUNTS.some(a => a.code === code);
}

// Parse date string in various formats to ISO string
function parseDate(dateStr) {
  if (!dateStr) return new Date().toISOString();
  // Already ISO format
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) return d.toISOString();
  }
  // DD-MM-YYYY or DD/MM/YYYY (Dutch format)
  const match = dateStr.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
  if (match) {
    const d = new Date(`${match[3]}-${match[2].padStart(2, '0')}-${match[1].padStart(2, '0')}T12:00:00Z`);
    if (!isNaN(d.getTime())) return d.toISOString();
  }
  // Try native parse as fallback
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) return d.toISOString();
  // Last resort: use today
  return new Date().toISOString();
}

export function BankStatements() {
  const { selectedEntity, isDutchEntity } = useEntity();
  const { t, language } = useLanguage();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [extractedTransactions, setExtractedTransactions] = useState([]);
  const [edits, setEdits] = useState({});
  const [batchId, setBatchId] = useState(null);
  const [savedImports, setSavedImports] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [bookingAll, setBookingAll] = useState(false);

  // Load saved imports on mount
  useEffect(() => {
    if (!selectedEntity) return;
    fetch(`${API_URL}/api/bank-imports?entity_id=${selectedEntity.entity_id}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(data => setSavedImports(data.filter(imp => imp.transactions.some(t => !t.booked))))
      .catch(() => {});
  }, [selectedEntity]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') setFile(droppedFile);
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) setFile(selectedFile);
  };

  const initEdits = (txns) => {
    const init = {};
    txns.forEach((txn, idx) => {
      if (txn.booked) { init[idx] = { confirmed: true }; return; }
      const isIncome = txn.amount >= 0;
      const hasMapping = !!txn.account_code;
      init[idx] = {
        account_code: hasMapping ? txn.account_code : (isIncome ? '4000' : '7800'),
        vat_applicable: hasMapping ? (txn.vat_likely || false) : isDutchEntity,
        vat_rate: 21,
        confirmed: !!txn.booked,
        learned: hasMapping,
      };
    });
    return init;
  };

  const handleUpload = async () => {
    if (!file || !selectedEntity) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_id', selectedEntity.entity_id);
      const response = await fetch(`${API_URL}/api/process-bank-statement`, {
        method: 'POST', credentials: 'include', body: formData
      });
      if (response.ok) {
        const data = await response.json();
        const txns = data.transactions || [];
        setExtractedTransactions(txns);
        setBatchId(data.batch_id);
        setEdits(initEdits(txns));
        const learnedCount = txns.filter(t => t.account_code).length;
        toast.success(`${txns.length} transacties gevonden${learnedCount > 0 ? ` (${learnedCount} automatisch)` : ''}`);
      } else { toast.error('Fout bij verwerken PDF'); }
    } catch (error) { console.error(error); toast.error('Netwerkfout bij uploaden'); }
    finally { setUploading(false); }
  };

  const loadSavedImport = (imp) => {
    setExtractedTransactions(imp.transactions);
    setBatchId(imp.batch_id);
    setEdits(initEdits(imp.transactions));
    setShowSaved(false);
    toast.success(`Import geladen (${imp.transactions.filter(t => !t.booked).length} onverwerkt)`);
  };

  const updateEdit = (idx, field, value) => {
    setEdits(prev => ({ ...prev, [idx]: { ...prev[idx], [field]: value } }));
  };

  const confirmTransaction = async (idx) => {
    const txn = extractedTransactions[idx];
    const edit = edits[idx];
    if (!edit || edit.confirmed) return true;

    const absAmount = Math.abs(txn.amount);
    const isIncome = txn.amount >= 0;
    const currency = isDutchEntity ? 'EUR' : 'USD';
    const isBalance = isBalanceAccount(edit.account_code);

    let netAmount, vatAmount;
    if (!isBalance && edit.vat_applicable && edit.vat_rate > 0) {
      const vatRate = edit.vat_rate / 100;
      netAmount = Math.round((absAmount / (1 + vatRate)) * 100) / 100;
      vatAmount = Math.round((absAmount - netAmount) * 100) / 100;
    } else {
      netAmount = absAmount;
      vatAmount = 0;
    }

    let lines = [];
    if (isBalance) {
      // Balance sheet transaction: just Bank <-> selected account
      if (isIncome) {
        lines.push({ account_code: '1000', debit: absAmount, credit: 0 });
        lines.push({ account_code: edit.account_code, debit: 0, credit: absAmount });
      } else {
        lines.push({ account_code: edit.account_code, debit: absAmount, credit: 0 });
        lines.push({ account_code: '1000', debit: 0, credit: absAmount });
      }
    } else if (isIncome) {
      lines.push({ account_code: '1000', debit: absAmount, credit: 0 });
      lines.push({ account_code: edit.account_code, debit: 0, credit: netAmount });
      if (vatAmount > 0) lines.push({ account_code: '1700', debit: 0, credit: vatAmount });
    } else {
      lines.push({ account_code: edit.account_code, debit: netAmount, credit: 0 });
      if (vatAmount > 0) lines.push({ account_code: '1600', debit: vatAmount, credit: 0 });
      lines.push({ account_code: '1000', debit: 0, credit: absAmount });
    }

    try {
      const res = await fetch(`${API_URL}/api/journal-entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          entity_id: selectedEntity.entity_id,
          date: parseDate(txn.date),
          description: txn.description || 'Bank transactie',
          lines,
          source: 'bank_import',
          reference: txn.reference || null,
          currency
        })
      });

      if (res.ok) {
        // Save category mapping for learning (skip for balance accounts)
        if (!isBalance) {
          fetch(`${API_URL}/api/save-category-mapping`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              entity_id: selectedEntity.entity_id,
              description_pattern: txn.description,
              category: edit.account_code,
              account_code: edit.account_code,
              vat_applicable: edit.vat_applicable,
              vat_rate: edit.vat_rate
            })
          }).catch(() => {});
        }
        // Mark as booked in database
        if (batchId) {
          fetch(`${API_URL}/api/bank-imports/${batchId}/mark-booked?idx=${idx}`, {
            method: 'PATCH', credentials: 'include'
          }).catch(() => {});
        }
        updateEdit(idx, 'confirmed', true);
        return true;
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(`Fout bij boeking #${idx + 1}: ${err.detail || 'Onbekende fout'}`);
        return false;
      }
    } catch (error) {
      console.error(error);
      toast.error(`Netwerkfout bij boeking #${idx + 1}`);
      return false;
    }
  };

  const confirmAll = async () => {
    setBookingAll(true);
    let success = 0, failed = 0;
    for (let idx = 0; idx < extractedTransactions.length; idx++) {
      if (edits[idx]?.confirmed) continue;
      const ok = await confirmTransaction(idx);
      if (ok) success++; else failed++;
    }
    setBookingAll(false);
    if (failed === 0) {
      toast.success(`Alle ${success} transacties succesvol geboekt`);
    } else {
      toast.error(`${success} geboekt, ${failed} mislukt. Controleer de fouten.`);
    }
  };

  const sym = isDutchEntity ? '\u20AC' : '$';
  const unbookedCount = Object.values(edits).filter(e => !e.confirmed).length;

  if (!selectedEntity) {
    return (<div className="flex items-center justify-center h-64"><p className="text-muted-foreground">Selecteer een entiteit</p></div>);
  }

  return (
    <div className="space-y-6" data-testid="bank-statements-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {t('nav.bankStatements')}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">{selectedEntity.name}</p>
      </div>

      {/* Saved imports banner */}
      {savedImports.length > 0 && extractedTransactions.length === 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    {savedImports.length} onafgeronde import{savedImports.length > 1 ? 'en' : ''} gevonden
                  </p>
                  <p className="text-xs text-amber-600">Klik om verder te gaan met classificeren en boeken</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowSaved(!showSaved)} data-testid="show-saved-imports">
                {showSaved ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </div>
            {showSaved && (
              <div className="mt-3 space-y-2">
                {savedImports.map(imp => {
                  const unbooked = imp.transactions.filter(t => !t.booked).length;
                  return (
                    <button key={imp.batch_id} onClick={() => loadSavedImport(imp)}
                      className="w-full text-left p-3 rounded-lg bg-white border hover:border-amber-400 transition-colors"
                      data-testid={`load-import-${imp.batch_id}`}>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">{imp.transactions.length} transacties</span>
                        <span className="text-xs text-amber-600">{unbooked} onverwerkt</span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(imp.created_at).toLocaleString('nl-NL')}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upload Zone */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">PDF Bankafschrift Uploaden</CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer hover:border-[hsl(var(--brand))] transition-colors"
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
            onClick={() => document.getElementById('pdf-upload').click()}
          >
            <input type="file" accept=".pdf" onChange={handleFileChange} className="hidden" id="pdf-upload" data-testid="pdf-upload-input" />
            <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">{file ? file.name : 'Sleep een PDF hier of klik om te uploaden'}</p>
          </div>
          {file && (
            <div className="mt-4 flex justify-center">
              <Button onClick={handleUpload} disabled={uploading} className="btn-brand" data-testid="process-pdf-btn">
                {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verwerken...</> : <><FileText className="w-4 h-4 mr-2" /> Verwerk PDF</>}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Extracted Transactions */}
      {extractedTransactions.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">
                Gevonden Transacties ({extractedTransactions.length})
              </CardTitle>
              {unbookedCount > 0 && (
                <p className="text-xs text-muted-foreground mt-1">{unbookedCount} nog te boeken</p>
              )}
            </div>
            {unbookedCount > 0 && (
              <Button onClick={confirmAll} className="btn-brand" disabled={bookingAll} data-testid="confirm-all-btn">
                {bookingAll
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Boeken...</>
                  : <><Check className="w-4 h-4 mr-2" /> Alles Boeken</>}
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            {extractedTransactions.map((txn, idx) => {
              const edit = edits[idx] || {};
              if (edit.confirmed) {
                return (
                  <div key={idx} className="border rounded-lg p-3 bg-[#6EC998]/10 border-[#6EC998]/30" data-testid={`txn-row-${idx}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground">{txn.date}</span>
                        <span className={`font-mono font-semibold ${txn.amount >= 0 ? 'text-[#6EC998]' : 'text-red-500'}`}>
                          {txn.amount >= 0 ? '+' : ''}{sym}{Math.abs(txn.amount).toFixed(2)}
                        </span>
                      </div>
                      <span className="flex items-center gap-1 text-[#6EC998] text-sm font-medium">
                        <Check className="w-4 h-4" /> Geboekt
                      </span>
                    </div>
                    <p className="text-sm truncate mt-1">{txn.description}</p>
                  </div>
                );
              }

              const isIncome = txn.amount >= 0;
              const isBal = isBalanceAccount(edit.account_code);
              const accountList = isIncome
                ? [...REVENUE_ACCOUNTS, { code: '_sep_bal', name: '--- Balansrekeningen ---', disabled: true }, ...BALANCE_ACCOUNTS]
                : [...EXPENSE_ACCOUNTS, { code: '_sep_bal', name: '--- Balansrekeningen ---', disabled: true }, ...BALANCE_ACCOUNTS];

              return (
                <div key={idx} className="border rounded-lg p-4" data-testid={`txn-row-${idx}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="text-xs text-muted-foreground">{txn.date}</span>
                        <span className={`font-mono font-semibold ${isIncome ? 'text-[#6EC998]' : 'text-red-500'}`}>
                          {isIncome ? '+' : ''}{sym}{Math.abs(txn.amount).toFixed(2)}
                        </span>
                        {edit.learned && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[#8387e7]/10 text-[#8387e7]">herkend</span>
                        )}
                      </div>
                      <p className="font-medium text-sm truncate">{txn.description}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-end gap-3 mt-3 pt-3 border-t border-dashed">
                    <div className="flex-1 min-w-[160px]">
                      <Label className="text-xs">Rekening</Label>
                      <Select value={edit.account_code} onValueChange={v => { if (v !== '_sep_bal') updateEdit(idx, 'account_code', v); }}>
                        <SelectTrigger className="h-9" data-testid={`txn-${idx}-account`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {accountList.map(acc => (
                            acc.disabled
                              ? <div key={acc.code} className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">{acc.name}</div>
                              : <SelectItem key={acc.code} value={acc.code}>{acc.code} {acc.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {isDutchEntity && !isBal && (
                      <div className="flex items-center gap-2 pb-1">
                        <Switch checked={edit.vat_applicable} onCheckedChange={v => updateEdit(idx, 'vat_applicable', v)} data-testid={`txn-${idx}-vat`} />
                        <Label className="text-xs">21% BTW</Label>
                      </div>
                    )}
                    {isBal && (
                      <span className="text-xs text-muted-foreground pb-2">Balansrekening (geen BTW)</span>
                    )}

                    <Button size="sm" onClick={() => confirmTransaction(idx)} className="btn-brand" data-testid={`confirm-txn-${idx}`}>
                      <Check className="w-4 h-4 mr-1" /> Boeken
                    </Button>
                  </div>

                  <PreviewJournal
                    amount={Math.abs(txn.amount)}
                    isIncome={isIncome}
                    accountCode={edit.account_code}
                    vatApplicable={!isBal && edit.vat_applicable}
                    vatRate={edit.vat_rate || 21}
                    sym={sym}
                    isBalance={isBal}
                  />
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function PreviewJournal({ amount, isIncome, accountCode, vatApplicable, vatRate, sym, isBalance }) {
  const ALL_NAMES = {
    '1000': 'Bank', '1100': 'Spaarrekening', '1300': 'Debiteuren', '1350': 'Overige vorderingen',
    '1400': 'Crediteuren', '1600': 'BTW te vorderen', '1700': 'BTW te betalen',
    '1750': 'Vooruitontvangen omzet', '1900': 'Intercompany',
    '0300': 'Deelnemingen', '0500': 'Lening', '0510': 'Achtergestelde lening',
    '4000': 'Omzet diensten', '4010': 'Omzet SaaS', '4020': 'Omzet consultancy', '4030': 'Overige omzet',
    '4100': 'Hosting & infra', '4200': 'Uitbesteed werk', '4300': 'Overige directe kosten',
    '4400': 'Advertentiekosten',
    '7000': 'Salarissen', '7010': 'Sociale lasten', '7020': 'Pensioen', '7100': 'Freelancers',
    '7500': 'Huisvesting', '7600': 'Kantoor', '7700': 'Software / ICT',
    '7800': 'Algemene kosten', '7900': 'Advieskosten',
    '8000': 'Afschrijvingen', '8100': 'Rentelasten', '8200': 'Bankkosten',
  };

  let netAmount, vatAmount;
  if (!isBalance && vatApplicable && vatRate > 0) {
    const rate = vatRate / 100;
    netAmount = Math.round((amount / (1 + rate)) * 100) / 100;
    vatAmount = Math.round((amount - netAmount) * 100) / 100;
  } else {
    netAmount = amount;
    vatAmount = 0;
  }

  let lines;
  if (isBalance) {
    lines = isIncome
      ? [{ acc: '1000', name: 'Bank', d: amount, c: 0 }, { acc: accountCode, name: ALL_NAMES[accountCode] || '', d: 0, c: amount }]
      : [{ acc: accountCode, name: ALL_NAMES[accountCode] || '', d: amount, c: 0 }, { acc: '1000', name: 'Bank', d: 0, c: amount }];
  } else if (isIncome) {
    lines = [
      { acc: '1000', name: 'Bank', d: amount, c: 0 },
      { acc: accountCode, name: ALL_NAMES[accountCode] || '', d: 0, c: netAmount },
      ...(vatAmount > 0 ? [{ acc: '1700', name: 'BTW te betalen', d: 0, c: vatAmount }] : []),
    ];
  } else {
    lines = [
      { acc: accountCode, name: ALL_NAMES[accountCode] || '', d: netAmount, c: 0 },
      ...(vatAmount > 0 ? [{ acc: '1600', name: 'BTW te vorderen', d: vatAmount, c: 0 }] : []),
      { acc: '1000', name: 'Bank', d: 0, c: amount },
    ];
  }

  return (
    <div className="mt-2 bg-muted/40 rounded-lg p-2 text-xs">
      <table className="w-full">
        <thead>
          <tr className="text-muted-foreground">
            <th className="text-left font-normal">Rekening</th>
            <th className="text-right font-normal w-20">Debet</th>
            <th className="text-right font-normal w-20">Credit</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i}>
              <td className="py-0.5"><span className="text-muted-foreground">{l.acc}</span> {l.name}</td>
              <td className="text-right tabular-nums">{l.d > 0 ? `${sym}${l.d.toFixed(2)}` : ''}</td>
              <td className="text-right tabular-nums">{l.c > 0 ? `${sym}${l.c.toFixed(2)}` : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
